import { useQuery } from "@apollo/react-hooks";
import { Card, Loading, Tabs } from "@shopify/polaris";
import { gql } from "apollo-boost";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  convertObjectToParams,
  convertStringToObject,
  convertToOffset,
  convertToPaged,
  getCookie,
  handleError,
  matchPathName,
  removeFieldWithoutFilter,
  setCookie,
} from "../../../helper";
import history from "../../../history";
import { COMMON_FILTER, USER_ROLE } from "../../../variable";
import { EmptyStatePolaris } from "../../shared/EmptyStatePolaris";
import { SkeletonPagePolaris } from "../../shared/SkeletonPagePolaris";
import { DataTableTransactionsPolaris } from "./DataTableTransactionsPolaris";
import { TransactionsFilterPolaris } from "./TransactionsFilterPolaris";

const QUERY_TRANSACTIONS = gql`
  query transactons($filter: TransactionFilter) {
    transactions(filter: $filter) {
      total
      nodes {
        id
        amount
        journal {
          refId
          comment
          status
          type
        }
        createdAt
        account {
          id
          firstName
          lastName
        }
        creator {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const ACTIONS = [
  { key: "Order", name: "Paid For Item" },
  { key: "Refund", name: "Refund" },
];

const FIELD_FILTER = [
  ...COMMON_FILTER,
  "status",
  "creatorId",
  "accountId",
  "kind",
];

export const TransactionsPolaris = ({ path }) => {
  const isMatchPathName = useMemo(() => matchPathName(path), [path]);
  const initFilter = useMemo(() => {
    let initialFilter = {
      ...convertStringToObject(history.location.search),
    };
    if (initialFilter) {
      if (initialFilter.kind) {
        initialFilter.kind = initialFilter.kind.split(",");
      }
      let limit = initialFilter.limit;
      limit = Number(limit);
      if (!limit) {
        limit = 20;
      }
      initialFilter.limit = limit;

      if (initialFilter.paged) {
        initialFilter.offset = convertToOffset(
          initialFilter.limit,
          Math.round(initialFilter.paged),
        );
      }
      initialFilter = removeFieldWithoutFilter(initialFilter, FIELD_FILTER);
    }
    return initialFilter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMatchPathName]);
  // State
  const [selectedTab, setSelectedTab] = useState(0);
  const [filter, setFilter] = useState({
    limit: Number(getCookie("perPageTransactions")) || 20,
    offset: 0,
    search: null,
    status: null,
    creatorId: null,
    accountId: null,
    kind: ACTIONS.map((action) => action.key),
    viewBySeller: true,
    ...initFilter,
  });

  // query
  const { data, loading, error } = useQuery(QUERY_TRANSACTIONS, {
    variables: {
      filter,
    },
  });

  // Handle action
  useEffect(() => {
    let { offset, limit, viewBySeller, ...rest } = filter;

    let params = null;
    let paged = convertToPaged(limit, offset);
    params = convertObjectToParams({
      limit,
      paged,
      ...rest,
    });
    history.push(`${history.location.pathname}?${params}`);
  }, [filter]);

  const handleTabChange = useCallback((selectedIndex) => {
    setSelectedTab(selectedIndex);
    setFilter((prev) => ({
      ...prev,
      status: null,
      creatorId: null,
      accountId: null,
      kind: ACTIONS.map((action) => action.key),
      offset: 0,
      viewBySeller: selectedIndex ? false : true,
    }));
  }, []);

  const tabs = [
    {
      id: "seller-transactions",
      content: "Seller transactions",
      accessibilityLabel: "Seller transactions",
    },
    {
      id: "supplier-transactions",
      content: "Supplier transactions",
      accessibilityLabel: "Supplier transactions",
    },
  ];
  const role = selectedTab ? USER_ROLE.Supplier : USER_ROLE.Seller;

  // Markup
  const loadingMarkup = loading && <Loading />;

  return (
    <React.Fragment>
      {loadingMarkup}
      <Card sectioned>
        <TransactionsFilterPolaris
          role={role}
          filter={filter}
          onChange={({ search, selectId, status, kind }) => {
            let selectedId = selectedTab ? "accountId" : "creatorId";
            setFilter((prev) => {
              if (
                !isEqual(prev[selectedId], selectId) ||
                !isEqual(prev.search, search) ||
                !isEqual(prev.status, status) ||
                !isEqual(prev.kind, kind)
              ) {
                return {
                  ...prev,
                  [selectedId]: selectId,
                  search,
                  status,
                  kind: kind.length
                    ? kind
                    : ACTIONS.map((action) => action.key),
                  offset: 0,
                };
              }
              return prev;
            });
          }}
        />
      </Card>
      <Card>
        {error && <div>Error: {handleError(error.toString())}</div>}
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
          {loading ? (
            <SkeletonPagePolaris />
          ) : data?.transactions?.nodes?.length > 0 ? (
            <DataTableTransactionsPolaris
              role={role}
              data={data}
              filter={filter}
              setFilter={(offset, limit) => {
                setFilter((prev) => {
                  if (
                    !isEqual(prev.offset, offset) ||
                    !isEqual(prev.limit, limit)
                  ) {
                    return {
                      ...prev,
                      offset,
                      limit,
                    };
                  }
                  return prev;
                });
                setCookie("perPageTransactions", limit, 100);
              }}
            />
          ) : (
            <EmptyStatePolaris />
          )}
        </Tabs>
      </Card>
    </React.Fragment>
  );
};
